import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { ANALYTICS_EVENTS, trackEvent } from "utils/analytics";
import { useFloorPlanComparison } from "../useFloorPlanComparison";
import type {
  UseComparedPlanSelectionArgs,
  UseComparedPlanSelectionResult,
} from "./types";

export const useComparedPlanSelection = ({
  basePlanRecord,
  planRecords,
}: UseComparedPlanSelectionArgs): UseComparedPlanSelectionResult => {
  const { t } = useTranslation();

  const [pickedPlanId, setPickedPlanId] = useState<number | null>(null);

  const { rememberedComparedPlanId, saveComparedPlan } = useFloorPlanComparison(
    { baseFloorPlanId: basePlanRecord.id },
  );

  const candidateRecords = useMemo(
    () => planRecords.filter((record) => record.id !== basePlanRecord.id),
    [planRecords, basePlanRecord.id],
  );

  const comparedPlanRecord = useMemo(() => {
    const selectedPlanId = pickedPlanId ?? rememberedComparedPlanId;
    if (selectedPlanId === null) return null;

    return (
      candidateRecords.find((record) => record.id === selectedPlanId) ?? null
    );
  }, [pickedPlanId, rememberedComparedPlanId, candidateRecords]);

  const selectComparedPlan = useCallback(
    async (comparedFloorPlanId: number) => {
      if (comparedFloorPlanId === comparedPlanRecord?.id) return;

      const previousPickedPlanId = pickedPlanId;
      setPickedPlanId(comparedFloorPlanId);
      try {
        await saveComparedPlan(comparedFloorPlanId);
        trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_COMPARED_PLAN_SELECTED);
      } catch {
        setPickedPlanId(previousPickedPlanId);
        toast.error(t("smartSurveyor.planComparison.saveFailed"));
      }
    },
    [comparedPlanRecord, pickedPlanId, saveComparedPlan, t],
  );

  return {
    candidateRecords,
    comparedPlanRecord,
    selectComparedPlan,
  };
};
